import React, { useState } from 'react';
import { Box, Text, Button, Stack } from '@stitch-fix/mode-react';
import { useRouter } from 'next/router';
import {
  useScreenViewEvent,
  ScreenViewEvent,
} from '@stitch-fix/event-reporter';
import ReOnboardingSelfie from './ReOnboardingSelfie';
import ReOnboardingFullBody from './ReOnboardingFullBody';
import { useVisionOnboardingCompleteMutation } from './graphql/visionOnboardingComplete';
import { OnboardingNav } from './OnboardingNav';
import { useToast } from '../../../utils/contexts/toast-provider';
import { useGetClientSelfieQuery } from './graphql/getClientSelfie';
import { SupportedBusinessLine } from './Onboarding';

interface OnboardingFailedProps {
  businessLine: SupportedBusinessLine;
  onboardingFailedMessage?: string;
  showOnboarderHeader?: boolean;
}

export const OnboardingFailed: React.FC<OnboardingFailedProps> = ({
  businessLine,
  onboardingFailedMessage = 'It seems like we hit a snag last time. Upload new pictures to see how you’ll look in fresh, Stylist-approved outfits.',
  showOnboarderHeader = true,
}) => {
  const router = useRouter();
  const { data: selfieData } = useGetClientSelfieQuery();
  const hasSelfieImage = Boolean(selfieData?.client?.selfieImageUrl);
  const [isUploading, setIsUploading] = useState(false);
  const { addToast } = useToast();

  const { EventProvider } = useScreenViewEvent({
    schema: 'screen_view' as const,
    name: 'vision_onboarding_retry' as ScreenViewEvent['name'],
    source_app: 'categories-next-ui' as const,
  });

  const [completeOnboardingMutation] = useVisionOnboardingCompleteMutation({
    onCompleted: data => {
      const result = data?.visionOnboardingComplete?.result;

      if (result?.__typename === 'VisionOnboardingCompleteSuccess') {
        router.push('/gallery');
      } else if (result?.__typename === 'VisionOnboardingCompleteError') {
        setIsUploading(false);
      }
    },
    onError: () => {
      setIsUploading(false);
      addToast({
        delay: 6000,
        status: 'error',
        message: 'Something went wrong. Please try again.',
      });
    },
  });

  const handleFinish = () => {
    setIsUploading(true);
    completeOnboardingMutation();
  };

  const handleClose = () => {
    router.push('/home');
  };

  const isFinishButtonDisabled = !hasSelfieImage || isUploading;

  return (
    <EventProvider>
      <>
        {showOnboarderHeader && (
          <OnboardingNav showCloseButton onBackClick={handleClose} />
        )}
        <Box
          maxWidth={54}
          mx="auto"
          px={1.5}
          mb={3}
          display="flex"
          style={{
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <Box mb={1.5}>
            <Text setting="title-xlarge" color="blue-45" align="left">
              Let’s try this again
            </Text>
          </Box>
          <Box mb={2}>
            <Text setting="body-large">{onboardingFailedMessage}</Text>
          </Box>
          <Stack spacing={3}>
            <ReOnboardingSelfie businessLine={businessLine} />
            <ReOnboardingFullBody businessLine={businessLine} />
          </Stack>
          <Box mt={1.5}>
            <Button
              variant="filled-statement"
              width="fill"
              onClick={handleFinish}
              disabled={isFinishButtonDisabled}
            >
              {isUploading ? 'Uploading...' : 'Done'}
            </Button>
          </Box>
        </Box>
      </>
    </EventProvider>
  );
};

export default OnboardingFailed;
